'use client';

import { AlertTriangle } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { AngleUpload } from '@/features/angle';
import { LoadingSpinner } from '@/shared/ui/LoadingSpinner';
import { ErrorBoundary } from '@/shared/ui';
import { ConceptAngleType } from '@/shared/constants';
import type { AngleStoreResponse } from '@/types/concept';

interface MainAngleUploadModalProps {
  conceptId: string | undefined;
  /**
   * Project the upload belongs to. When the concept already has renders the
   * new photo goes to the project library (no conceptId) and a new concept is
   * created from it after validation.
   */
  projectId: string;
  conceptAnglesCount: number;
  hasCompletedRenders: boolean;
  isValidating: boolean;
  validationError: string | null;
  /**
   * Current count of angles in the project library. Used for pre-flight
   * limit check before upload.
   */
  currentProjectAngleLibraryCount?: number;
  onProjectAngleLibraryLimitReached?: () => void;
  onClose: () => void;
  onSuccess: (angleResponse: AngleStoreResponse | AngleStoreResponse[]) => void;
  onClearError: () => void;
  onLimitReached: () => void;
}

export function MainAngleUploadModal({
  conceptId,
  projectId,
  conceptAnglesCount,
  hasCompletedRenders,
  isValidating,
  validationError,
  currentProjectAngleLibraryCount,
  onProjectAngleLibraryLimitReached,
  onClose,
  onSuccess,
  onClearError,
  onLimitReached,
}: MainAngleUploadModalProps) {
  const t = useTranslations('concept');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-background rounded-lg p-6 max-w-lg w-full">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-sheet-title">{t('mainAngleUpload.title')}</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-muted-foreground/80"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {!isValidating && !validationError && (
          <p className="text-muted-foreground mb-4">
            {hasCompletedRenders
              ? t('mainAngleUpload.descriptionNewConcept')
              : t('mainAngleUpload.descriptionSetMain')}
          </p>
        )}

        {isValidating && (
          <div className="mb-4">
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="md" />
            </div>
            <p className="text-form-help text-muted-foreground text-center">
              {t('mainAngleUpload.validatingImage')}
            </p>
          </div>
        )}

        {validationError && (
          <div className="mb-4">
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-destructive mr-2" />
                <div className="flex-1">
                  <h3 className="text-form-label text-destructive">
                    {t('mainAngleUpload.validationFailedTitle')}
                  </h3>
                  <p className="mt-1 text-form-help text-destructive/90">
                    {validationError}
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={onClearError}
              className="mt-3 w-full bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90"
            >
              {t('mainAngleUpload.tryAnotherImage')}
            </button>
          </div>
        )}

        {!isValidating && !validationError && (
          <ErrorBoundary>
            <AngleUpload
              conceptId={hasCompletedRenders ? undefined : conceptId}
              projectId={projectId}
              onSuccess={onSuccess}
              onError={onClose}
              allowMultiple={false}
              showValidation={false}
              angleType={ConceptAngleType.MAIN}
              onLimitReached={onLimitReached}
              currentAngleCount={conceptAnglesCount - 1}
              currentProjectAngleLibraryCount={currentProjectAngleLibraryCount}
              onProjectAngleLibraryLimitReached={
                onProjectAngleLibraryLimitReached
              }
            />
          </ErrorBoundary>
        )}
      </div>
    </div>
  );
}
